import { AnimatePresence, motion } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useBelowMd } from '../../hooks/useMediaQuery';
import { Icon } from '../header/draft/Icon';

function volumeIconName(percent: number) {
  if (percent === 0) return 'volume_off';
  if (percent < 34) return 'volume_mute';
  if (percent < 67) return 'volume_down';
  return 'volume_up';
}

const popoverPanelClass = 'mini-player-volume-popover';

type MiniPlayerVolumeControlProps = {
  /** 0–1 */
  volume: number;
  onVolumeChange: (value: number) => void;
  className?: string;
};

export function MiniPlayerVolumeControl({ volume, onVolumeChange, className = '' }: MiniPlayerVolumeControlProps) {
  const { t } = useTranslation();
  const isMobileLayout = useBelowMd();
  const [volumeOpen, setVolumeOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  const volumePercent = Math.round(volume * 100);

  const updatePopoverPos = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPopoverStyle({
      position: 'fixed',
      bottom: window.innerHeight - rect.top + 8,
      right: window.innerWidth - rect.right,
      zIndex: 10000,
    });
  }, []);

  useLayoutEffect(() => {
    if (!volumeOpen) return;
    updatePopoverPos();
    window.addEventListener('resize', updatePopoverPos);
    window.addEventListener('scroll', updatePopoverPos, true);
    return () => {
      window.removeEventListener('resize', updatePopoverPos);
      window.removeEventListener('scroll', updatePopoverPos, true);
    };
  }, [volumeOpen, updatePopoverPos]);

  useEffect(() => {
    if (isMobileLayout) setVolumeOpen(false);
  }, [isMobileLayout]);

  useEffect(() => {
    if (!volumeOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setVolumeOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [volumeOpen]);

  if (isMobileLayout) return null;

  const popover = (
    <AnimatePresence>
      {volumeOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, y: 6, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.96 }}
          transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
          className={popoverPanelClass}
          style={popoverStyle}
          role="dialog"
          aria-label={t('miniPlayer.volume')}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2.5">
            <Icon name={volumeIconName(volumePercent)} className="text-on-surface-variant text-lg shrink-0" />
            <span className="text-xs font-semibold text-white/90">{t('miniPlayer.volume')}</span>
            <span className="ml-auto text-xs tabular-nums text-on-surface-variant">{volumePercent}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={volumePercent}
            onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
            className="mini-player-volume-slider w-full"
            style={{ '--fill': `${volumePercent}%` } as CSSProperties}
            aria-label={t('miniPlayer.volumeSlider')}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div ref={anchorRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setVolumeOpen((open) => !open);
        }}
        className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-colors lg:cursor-pointer ${
          volumeOpen ? 'text-white bg-white/10' : 'text-on-surface-variant lg:hover:text-white'
        }`}
        aria-label={t('miniPlayer.volume')}
        aria-expanded={volumeOpen}
        aria-haspopup="true"
      >
        <Icon name={volumeIconName(volumePercent)} className="text-xl sm:text-2xl" />
      </button>
      {typeof document !== 'undefined' && createPortal(popover, document.body)}
    </div>
  );
}
